import {
  ArrowDownTrayIcon,
  CheckIcon,
  LinkIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { Button, Typography } from '@material-tailwind/react';
import { useCallback, useEffect, useState } from 'react';
import html2canvas from 'html2canvas';
import {
  FreeTrialQuotaItem,
  FreeTrialRecitationResult,
  RecitationResultStatusEnum,
} from '../../constants/types/free-trial/type';
import FreeTrialResultCard from './FreeTrialResultCard';
import FreeTrialResultReport from './FreeTrialResultReport';
import {
  createShareUrl,
  downloadFile,
  waitForImages,
} from '../../utils/free-trial/utils';
import { useAppDispatch } from '../../store/hook';
import {
  createShareTokenWithConsent,
  getFreeTrialQuota,
  getFreeTrialRecitationResult,
  reportFreeTrialStepProgress,
} from '../../api/utils/free-trial/api';
import { openAlert } from '../../store/features/modalSlice';
import {
  copulaParticle,
  getErrorMessages,
  handleCopyClipBoard,
  removeSurname,
} from '../../api/utils/util-func';
import FreeTrialAttentionEffect from './FreeTrialAttentionEffect';
import { useNavigate } from 'react-router-dom';
import { useFreeTrialConfig } from '../../hooks/useFreeTrialConfig';

const FreeTrialResultStep = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const config = useFreeTrialConfig();

  const [shareFile, setShareFile] = useState<File | null>(null);
  const [isPreparingShare, setIsPreparingShare] = useState(true);

  const [isCreatingShareLink, setIsCreatingShareLink] = useState(false);

  const linkToken = sessionStorage.getItem('freeTrialLinkToken');

  useEffect(() => {
    if (!linkToken) return;

    const reportResultProgress = async () => {
      try {
        await reportFreeTrialStepProgress({
          linkToken,
          step: 'result',
        });
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
        console.error('결과 단계 진행 상태 저장 실패', error);
      }
    };

    void reportResultProgress();
  }, [linkToken]);

  const [freeTrialQuota, setFreeTrialQuota] =
    useState<FreeTrialQuotaItem | null>(null);

  useEffect(() => {
    if (!linkToken) return;

    const fetchreeTrialQuota = async () => {
      try {
        const data = await getFreeTrialQuota();

        setFreeTrialQuota(data);
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      }
    };

    void fetchreeTrialQuota();
  }, [linkToken]);

  const [freeTrialRecitationResult, setFreeTrialRecitationResult] =
    useState<FreeTrialRecitationResult | null>(null);

  const isPassed =
    freeTrialRecitationResult?.status === RecitationResultStatusEnum.PASSED;

  const remaining = freeTrialQuota?.remaining ?? 0;
  const canRetry = remaining > 0;

  const showRetryBanner = !isPassed && canRetry;
  const showAttemptsExhaustedBanner = !isPassed && !canRetry;

  useEffect(() => {
    if (!linkToken) return;

    const fetchFreeTrialRecitationResult = async () => {
      try {
        const data = await getFreeTrialRecitationResult(linkToken);

        setFreeTrialRecitationResult(data);
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      }
    };

    void fetchFreeTrialRecitationResult();
  }, [linkToken]);

  const createResultCardFile = useCallback(async () => {
    const card = document.getElementById('free-trial-result-card');

    if (!card) {
      throw new Error('결과 카드를 찾을 수 없습니다.');
    }

    await document.fonts.ready;
    await waitForImages(card);

    const canvas = await html2canvas(card, {
      scale: 2,
      useCORS: true,
      backgroundColor: null,
      logging: false,

      onclone: (clonedDocument) => {
        // html2canvas에서 아래로 밀리는 텍스트 보정
        const offsetElements = clonedDocument.querySelectorAll<HTMLElement>(
          '[data-capture-offset]',
        );

        offsetElements.forEach((element) => {
          const offset = Number(element.dataset.captureOffset ?? 0);

          element.style.position = 'relative';
          element.style.top = `${offset}px`;
        });

        // 캐치프레이즈 ↔ 유형명 간격
        const catchphrase = clonedDocument.querySelector<HTMLElement>(
          '[data-capture-role="catchphrase"]',
        );

        const resultName = clonedDocument.querySelector<HTMLElement>(
          '[data-capture-role="result-name"]',
        );

        if (catchphrase) {
          catchphrase.style.marginTop = '8px';
        }

        if (resultName) {
          resultName.style.marginTop = '2px';
        }

        // 유형명 ↔ 캐릭터 이미지 간격
        const characterImageWrap = clonedDocument.querySelector<HTMLElement>(
          '[data-capture-role="character-image-wrap"]',
        );

        if (characterImageWrap) {
          characterImageWrap.style.marginTop = '28px';
        }

        // 성경 구절 ↔ 태그 간격
        const tagsWrap = clonedDocument.querySelector<HTMLElement>(
          '[data-capture-role="tags-wrap"]',
        );

        if (tagsWrap) {
          tagsWrap.style.marginTop = '12px';
        }
      },
    });

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('카드 이미지 생성에 실패했습니다.'));
          return;
        }

        resolve(blob);
      }, 'image/png');
    });

    return new File(
      [blob],
      `${freeTrialRecitationResult.childName}_암송유형.png`,
      {
        type: 'image/png',
      },
    );
  }, [freeTrialRecitationResult?.childName]);

  // navigator.share는 클릭 직후 바로 호출되는 편이 안전하므로
  // 공유 파일을 결과 화면 진입 시 미리 만들어 둔다.
  useEffect(() => {
    if (!isPassed) {
      setShareFile(null);
      setIsPreparingShare(false);
      return;
    }

    let isMounted = true;

    const prepareShareFile = async () => {
      try {
        setIsPreparingShare(true);

        const file = await createResultCardFile();

        if (!isMounted) return;

        setShareFile(file);
      } catch (error) {
        console.error('공유 이미지 생성 실패', error);

        if (isMounted) {
          setShareFile(null);
        }
      } finally {
        if (isMounted) {
          setIsPreparingShare(false);
        }
      }
    };

    void prepareShareFile();

    return () => {
      isMounted = false;
    };
  }, [createResultCardFile, isPassed]);

  const handleShareCard = () => {
    if (!shareFile) return;

    const shareData: ShareData = {
      files: [shareFile],
      title: `${freeTrialRecitationResult.childName}의 암송 유형`,
      text: `${freeTrialRecitationResult.childName}의 암송 유형 결과를 확인해 보세요!`,
    };

    const canShareFile =
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function' &&
      navigator.canShare({
        files: [shareFile],
      });

    if (!canShareFile) {
      return;
    }

    navigator.share(shareData).catch((error) => {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      console.error('카드 이미지 공유 실패', error);
    });
  };

  const handleCopyLink = async () => {
    if (!linkToken || isCreatingShareLink) return;

    try {
      setIsCreatingShareLink(true);

      const { shareToken } = await createShareTokenWithConsent(linkToken);

      const shareUrl = createShareUrl(shareToken);

      const result = await handleCopyClipBoard(shareUrl);

      dispatch(
        openAlert({
          message: result
            ? '공유 링크가 복사되었습니다.'
            : '공유 링크 복사에 실패했습니다.',
        }),
      );
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
      console.error('공유 링크 생성 실패', error);
    } finally {
      setIsCreatingShareLink(false);
    }
  };

  const handleSaveCardImage = async () => {
    try {
      const file = shareFile ?? (await createResultCardFile());

      downloadFile(file);
    } catch (error) {
      console.error('카드 이미지 저장 실패', error);
    }
  };

  const openKakaoLink = () => {
    window.open(config.FREE_TRIAL_KAKAO, '_blank');
  };

  const handleRetryApply = () => {
    navigate('/free-trial/apply');
  };

  const openSeminarVideoLink = () => {
    window.open(config.FREE_TRIAL_SEMINAR_VIDEO, '_blank');
  };

  if (!freeTrialRecitationResult) {
    return (
      <div className="flex min-h-[calc(100dvh-58px)] items-center justify-center">
        <Typography className="text-sm text-gray-900">
          AI 인증 정보를 불러오고 있어요.
        </Typography>
      </div>
    );
  }

  return (
    <div className="mt-5">
      {/* FAILED + 1회 남음 */}
      {showRetryBanner && (
        <section className="overflow-hidden rounded-[22px] bg-[linear-gradient(135deg,#6F42F5_0%,#5A35E8_100%)] px-5 py-5 text-white shadow-[0_10px_24px_rgba(88,57,180,0.20)] md:px-7 md:py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <span className="inline-flex rounded-full bg-[#FFD928] px-2.5 py-1 text-[10px] font-extrabold leading-none text-[#4B3500] md:text-[11px]">
                무료체험 기회 1회 남음
              </span>

              <Typography className="mt-3 text-[24px] font-black leading-[1.22] text-white md:text-[30px]">
                무료체험 기회가 1회 남았어요.
                <br />
                다시 신청해서 도전해 보세요!
              </Typography>

              <Typography className="mt-2 text-[12px] font-medium leading-5 text-white/85 md:text-[13px]">
                다시 신청하면 새로운 3일이 시작되고, AI 인증 응시 기회도 2회
                다시 생겨요. 이번 기록이 있으니 다음엔 더 가까워질 거예요.
              </Typography>
            </div>

            <Button
              ripple={false}
              onClick={handleRetryApply}
              className="h-12 shrink-0 rounded-full bg-white px-5 py-0 text-[13px] font-extrabold normal-case text-[#5B35DD] shadow-none transition-all hover:bg-[#F5F1FF] hover:shadow-none focus:shadow-none md:h-[52px] md:px-6 md:text-[14px]"
            >
              무료체험 다시 신청하기
            </Button>
          </div>
        </section>
      )}

      {/* FAILED + 0회 남음 */}
      {showAttemptsExhaustedBanner && (
        <div className="space-y-3">
          {/* 응시 완료 요약 */}
          <section className="rounded-[22px] bg-[#FFF4D2] px-4 py-4 md:px-5 md:py-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[#E7D9A7] bg-white md:h-14 md:w-14">
                  <img
                    src="/images/free-trial/bready.png"
                    alt="bready"
                    className="h-full w-full object-contain"
                  />
                </div>

                <div className="min-w-0">
                  <Typography className="text-[10px] font-extrabold text-[#7C6428] md:text-[11px]">
                    AI 인증제 2 / 2회 응시
                  </Typography>

                  <Typography className="mt-0.5 text-[20px] font-black leading-[1.25] text-[#3A2B0D] md:text-[24px]">
                    응시 기회를 모두 사용했어요 ㅠㅠ
                  </Typography>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="inline-flex h-8 items-center rounded-full bg-white px-3 text-[10px] font-extrabold text-[#6737F4] md:text-[11px]">
                  ✓ 수업하기
                </span>

                <span className="inline-flex h-8 items-center rounded-full bg-white px-3 text-[10px] font-extrabold text-[#6737F4] md:text-[11px]">
                  ✓ 온라인 복습
                </span>

                <span className="inline-flex h-8 items-center rounded-full bg-white px-3 text-[10px] font-extrabold text-[#8A5A00] md:text-[11px]">
                  AI 인증제 2회 도전
                </span>
              </div>
            </div>
          </section>

          {/* 재도전 / 상담 안내 */}
          <section className="rounded-[22px] border border-[#D9CEFF] bg-white px-4 py-5 shadow-[0_4px_14px_rgba(88,57,180,0.04)] md:px-5 md:py-6">
            <div className="inline-flex rounded-full bg-[#F1EDFF] px-3 py-1.5">
              <span className="text-[10px] font-extrabold text-[#6737F4] md:text-[11px]">
                무료체험 기회 2회 모두 사용
              </span>
            </div>

            <Typography className="mt-2 text-[21px] font-black leading-[1.3] text-gray-900 md:text-[26px]">
              {copulaParticle(
                removeSurname(freeTrialRecitationResult.childName),
              )}
              의 도전은 여기서 끝이 아니에요
            </Typography>

            <Typography className="mt-2 text-[12px] font-medium leading-5 text-gray-600 md:text-[13px] md:leading-6">
              무료체험 기회는 모두 사용했지만, 두 번이나 끝까지 도전한
              것만으로도 충분히 멋져요. 어디서 막혔는지 선생님이 기록을 함께
              보고,{' '}
              {copulaParticle(
                removeSurname(freeTrialRecitationResult.childName),
              )}
              의 속도에 맞는 연습 방법을 알려 드릴게요.
            </Typography>

            <div className="mt-4 grid gap-2 sm:grid-cols-2 md:mt-5 md:gap-3">
              <Button
                ripple={false}
                onClick={openKakaoLink}
                className="flex min-h-[58px] items-center justify-start rounded-xl bg-[#FFE500] px-4 py-2 text-left normal-case text-[#332A00] shadow-none transition-colors hover:bg-[#F7DC00] hover:shadow-none focus:shadow-none md:min-h-[64px]"
              >
                <div>
                  <div className="text-[12px] font-black md:text-[13px]">
                    카카오톡 상담하기
                  </div>

                  <div className="mt-1 text-[9px] font-medium leading-4 text-[#665300] md:text-[10px]">
                    도전 기록을 보고 우리 아이에게 맞는 시작 레벨과 학습법을
                    안내해 드려요
                  </div>
                </div>
              </Button>

              <Button
                ripple={false}
                onClick={openSeminarVideoLink}
                className="flex min-h-[58px] items-center justify-start rounded-xl bg-[#F1EDFF] px-4 py-2 text-left normal-case text-[#2F2745] shadow-none transition-colors hover:bg-[#E9E2FF] hover:shadow-none focus:shadow-none md:min-h-[64px]"
              >
                <div>
                  <div className="text-[12px] font-black md:text-[13px]">
                    설명회 영상보기
                  </div>

                  <div className="mt-1 text-[9px] font-medium leading-4 text-[#655D78] md:text-[10px]">
                    정식 과정에서는 수업 영상과 학습으로 어떻게 함께하는지 먼저
                    확인해 보세요
                  </div>
                </div>
              </Button>
            </div>
          </section>
        </div>
      )}

      {/* 완료 배너 */}
      {isPassed && (
        <section className="rounded-2xl bg-[#6737F4] px-5 py-5 text-white shadow-[0_6px_20px_rgba(40,25,100,0.08)] md:px-6 md:py-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Typography className="text-[11px] font-bold text-white/80 md:text-[12px]">
                무료체험 완료
              </Typography>

              <Typography className="mt-1 text-[24px] font-black leading-[1.25] text-white md:text-[30px]">
                {copulaParticle(
                  removeSurname(freeTrialRecitationResult.childName),
                )}
                가 3단계를 모두 해냈어요!
              </Typography>
            </div>

            <div className="flex flex-wrap gap-2">
              {['수업하기', '온라인 복습', 'AI 인증제'].map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[10px] font-extrabold text-[#4D2CD0] md:text-[11px]"
                >
                  <CheckIcon className="h-3 w-3 stroke-[3]" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 결과 영역 */}
      {isPassed && (
        <section className="mt-4 rounded-2xl bg-white px-5 py-7 shadow-[0_6px_20px_rgba(40,25,100,0.05)] md:px-8 md:py-8">
          <div className="grid gap-8 lg:grid-cols-[minmax(280px,420px)_minmax(0,1fr)] lg:items-center lg:gap-10">
            {/* 결과 카드 */}
            <FreeTrialResultCard
              freeTrialRecitationResult={freeTrialRecitationResult}
            />

            {/* 공유 영역 */}
            <div className="mx-auto w-full max-w-[460px] lg:mx-0">
              <Typography className="text-[11px] font-extrabold text-[#6737F4] md:text-[12px]">
                우리 아이 암송 유형
              </Typography>

              <Typography className="mt-2 text-[26px] font-black leading-[1.25] text-gray-900 md:text-[30px]">
                지금 바로 자랑해요!
              </Typography>

              <Typography className="mt-4 text-[13px] leading-6 text-gray-600 md:text-[14px] md:leading-7">
                암송 유형 카드가 이미지로 공유돼요. 받은 분은 젭스 소개와
                무료체험 신청 페이지로 연결돼요.
              </Typography>

              {/* 모바일 */}
              <div className="mt-6 grid grid-cols-2 gap-2.5 xl:hidden">
                <Button
                  ripple={false}
                  disabled={isCreatingShareLink}
                  onClick={handleCopyLink}
                  className="relative flex h-11 items-center justify-center gap-1.5 rounded-xl bg-[#6737F4] px-3 py-0 text-[12px] font-bold normal-case text-white shadow-none hover:bg-[#5425D6] hover:shadow-none focus:shadow-none disabled:bg-[#C7BAF5]"
                >
                  {!isCreatingShareLink && (
                    <FreeTrialAttentionEffect shine={false} />
                  )}

                  <span className="relative z-10 flex items-center gap-1.5">
                    <LinkIcon className="h-4 w-4" />
                    {isCreatingShareLink ? '링크 만드는 중...' : '링크 복사'}
                  </span>
                </Button>

                <Button
                  variant="outlined"
                  ripple={false}
                  disabled={!shareFile || isPreparingShare}
                  onClick={handleShareCard}
                  className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-[#DDD5F5] !bg-white px-3 py-0 text-[12px] font-bold normal-case text-gray-800 shadow-none hover:!bg-[#F7F4FF] hover:shadow-none focus:shadow-none disabled:border-gray-200 disabled:!bg-gray-50 disabled:text-gray-400"
                >
                  <ShareIcon className="h-4 w-4" />
                  {isPreparingShare ? '이미지 준비 중...' : '카드 이미지 공유'}
                </Button>
              </div>

              {/* PC */}
              <div className="mt-6 hidden grid-cols-2 gap-2.5 xl:grid">
                <Button
                  ripple={false}
                  disabled={isCreatingShareLink}
                  onClick={handleCopyLink}
                  className="relative flex h-11 items-center justify-center gap-1.5 rounded-xl bg-[#6737F4] px-3 py-0 text-[13px] font-bold normal-case text-white shadow-none hover:bg-[#5425D6] hover:shadow-none focus:shadow-none disabled:bg-[#C7BAF5]"
                >
                  {!isCreatingShareLink && (
                    <FreeTrialAttentionEffect shine={false} />
                  )}

                  <span className="relative z-10 flex items-center gap-1.5">
                    <LinkIcon className="h-4 w-4" />
                    {isCreatingShareLink ? '링크 만드는 중...' : '링크 복사'}
                  </span>
                </Button>

                <Button
                  variant="outlined"
                  ripple={false}
                  onClick={handleSaveCardImage}
                  className="flex h-11 items-center justify-center gap-1.5 rounded-xl border-[#DDD5F5] !bg-white px-3 py-0 text-[13px] font-bold normal-case text-gray-800 shadow-none hover:!bg-[#F7F4FF] hover:shadow-none focus:shadow-none"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  카드 이미지 저장
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 결과 리포트 */}
      {isPassed && (
        <FreeTrialResultReport
          freeTrialRecitationResult={freeTrialRecitationResult}
        />
      )}

      {/* 상담 CTA */}
      <section className="mt-4 rounded-2xl bg-[#4D23C8] px-5 py-5 text-white md:px-7 md:py-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div>
            <Typography className="text-[10px] font-medium text-white/75 md:text-[11px]">
              바이블 스토리 암송, 체험해 보니 어떠셨나요?
            </Typography>

            <Typography className="mt-1 text-[19px] font-black leading-[1.35] text-white md:text-[24px]">
              우리 아이에게 맞는 젭스 레벨, 1:1 상담으로 찾아 드려요
            </Typography>
          </div>

          <Button
            ripple={false}
            onClick={openKakaoLink}
            className="h-11 shrink-0 rounded-full bg-[#FEE500] px-6 py-0 text-[12px] font-extrabold normal-case text-[#191919] shadow-none hover:bg-[#FDDC00] hover:shadow-none focus:shadow-none md:text-[13px]"
          >
            카카오톡 상담하기
          </Button>
        </div>
      </section>
    </div>
  );
};

export default FreeTrialResultStep;
